import { colors } from '@pay24/common'
import { useTheme } from '@pay24/common/src/hooks'
import { useNavigationState } from '@react-navigation/native'
import { MenuItem } from '@ui-kitten/components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Animated,
  LayoutAnimation,
  Platform,
  Pressable,
  UIManager,
  View,
} from 'react-native'
import { CATEGORY_MAP } from './collapsable_menus'
import { MenuIcon } from './MenuIcon'
import { MenuTitle } from './MenuTitle'

interface CollapsableMenuProps {
  navigation: any
  menu: any
  onPress?: (path: string) => void
  isAuthorized: boolean
  isChargeOwner: boolean
  isChargeUserViewer: boolean
  isChargeUserAdmin: boolean
}

if (
  Platform.OS === 'android' &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true)
}

function useScreenFocused(screens: string[]) {
  return useNavigationState((state) => {
    if (!state) return false
    if (!state.index) {
      if (screens.includes(state.routes[0].state?.routes[0].name || ''))
        return true
    } else {
      if (screens.includes(state.routes[state.index].name)) return true
      let _state = state.routes[state.index].state

      while (_state?.routes) {
        const { routes, index = 0 } = _state
        if (screens.includes(routes[index].name)) return true
        _state = routes[index].state
      }
    }
    return false
  })
}

export const CollapsableMenu = (props: CollapsableMenuProps) => {
  const {
    navigation,
    menu,
    onPress,
    isAuthorized,
    isChargeOwner,
    isChargeUserViewer,
    isChargeUserAdmin,
  } = props
  const { isLight } = useTheme()
  const [expanded, setExpanded] = useState(false)

  const currentCategory = CATEGORY_MAP[menu.path].filter((c) => {
    const roles = c.roles || {}

    if (roles.authRequired && !isAuthorized) {
      return false
    }

    const hasRoleConditions =
      roles.isChargeOwner || roles.isChargeUserViewer || roles.isChargeUserAdmin

    if (!hasRoleConditions) {
      return true
    }

    return (
      (roles.isChargeOwner && isChargeOwner) ||
      (roles.isChargeUserViewer && isChargeUserViewer) ||
      (roles.isChargeUserAdmin && isChargeUserAdmin)
    )
  })

  const anyChildFocused = useScreenFocused(currentCategory.map((c) => c.path))

  const toggleCollapse = () => setExpanded(!expanded)

  useEffect(() => {
    if (anyChildFocused) {
      setExpanded(true)
    } else {
      setExpanded(false)
    }
  }, [anyChildFocused])

  const animation = useRef(new Animated.Value(0)).current

  useEffect(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)

    Animated.timing(animation, {
      toValue: expanded ? 1 : 0,
      duration: 100,
      useNativeDriver: false,
    }).start()
  }, [expanded])

  const navigate = useCallback(
    (screen: string) => {
      if (onPress) {
        onPress(menu.path)
      }
      navigation.navigate(menu.path, { screen })
    },
    [menu.path, navigation, onPress],
  )

  const containerHeight = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, currentCategory.length * 55],
  })

  const opacity = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 1],
  })

  return (
    <View
      style={{
        borderBottomWidth: 1,
        borderBottomColor: isLight
          ? 'rgba(0, 0, 0, 0.05)'
          : 'rgba(255, 255, 255, 0.05)',
      }}
    >
      <Pressable
        style={{
          paddingLeft: 16,
          paddingVertical: 12,
          backgroundColor: 'transparent',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        onPress={toggleCollapse}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <MenuIcon name={menu.icon} />
          <MenuTitle title={menu.title || menu.path} />
        </View>
        <View style={{ position: 'absolute', right: 10 }}>
          <MenuIcon name={expanded ? 'chevron-up' : 'chevron-down'} />
        </View>
      </Pressable>
      <Animated.View
        style={{
          paddingLeft: 22,
          maxHeight: containerHeight,
          opacity,
          overflow: 'hidden',
        }}
      >
        {currentCategory.map((item) => (
          <CollapsableMenuItem
            key={item.path}
            item={item}
            navigate={navigate}
          />
        ))}
      </Animated.View>
    </View>
  )
}

const CollapsableMenuItem = ({ item, navigate }) => {
  const active = useScreenFocused([item.path])

  const { isLight, primary } = useTheme()

  const textColorActive = primary
  const textColorInactive = isLight ? colors.black : colors.white
  return (
    <MenuItem
      key={item.path}
      testID={item.path}
      style={{
        paddingLeft: 16,
        backgroundColor: 'transparent',
      }}
      title={
        <MenuTitle
          title={item.title || item.path}
          color={active ? textColorActive : textColorInactive}
        />
      }
      onPress={() => navigate(item.path)}
    />
  )
}
